import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faBolt,
  faTag,
  faBuilding,
  faEnvelope,
  faKaaba,
  faFileInvoice,
  faBoxesStacked,
  faCalendarCheck,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrimaryImageUrl,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontProducts,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { listDashboardNewsPosts } from '../lib/news'

const WA_NUMBER = '6285704011028'
const LP_WALL_COUNT = 24
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const

const PRODUCT_NAME = 'Perlengkapan Haji Umroh'
const PRODUCT_IMAGE_TRANSFORM = { width: 480, format: 'webp', quality: 70 } as const

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// Testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

// Travel-agency FAQ: this buyer assembles per-jamaah kits in bulk before each
// departure season, so it answers per-kit pricing, composition, lead time,
// and PO/invoice questions.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Berapa harga perlengkapan jamaah per paket?',
    a: 'Harga dihitung dari item dan jumlah dalam setiap paket. Kirim jumlah jamaah serta komposisi paket agar admin dapat membuat quotation dengan rincian biaya per jamaah.',
  },
  {
    q: 'Item apa saja yang bisa disuplai untuk jamaah?',
    a: 'Pilihan produk mencakup mukena, kaos kaki salat muslimah, sarung lengan, manset, kaos tangan, handuk, dan head buff. Kirim komposisi paket yang dibutuhkan agar admin dapat memeriksa produk serta ketentuan campurnya.',
  },
  {
    q: 'Minimal ordernya berapa?',
    a: 'Minimum order mengikuti kategori produk. Handuk umumnya mulai 10 lusin dan kaos kaki umumnya 50 lusin. Kirim jumlah jamaah serta isi paket agar admin dapat memeriksa MOQ setiap produk.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Kapan sebaiknya order sebelum jadwal keberangkatan?',
    a: 'Kirim target penerimaan, jumlah jamaah, dan komposisi paket sejak awal. Admin akan memeriksa stok, waktu penyiapan, serta estimasi perjalanan sebelum travel mengunci jadwal.',
  },
  {
    q: 'Bisa pakai PO dan minta invoice resmi?',
    a: 'Bisa. Sampaikan format PO dan kebutuhan invoice saat meminta quotation untuk pengadaan travel.',
  },
  {
    q: 'Bisa minta sample dulu sebelum order rombongan?',
    a: 'Bisa. Biaya sample dihitung dari harga satuan dan ongkir. Tim travel dapat memeriksa bahan, ukuran, jahitan, serta kecocokan setiap produk sebelum memesan untuk seluruh jamaah.',
  },
  {
    q: 'Bisa kirim ke kota kami?',
    a: 'Bisa. Kirim alamat kantor travel dan jumlah paket. Admin akan membandingkan layanan ekspedisi, lalu mengirim nomor resi setelah barang diserahkan untuk pengiriman.',
  },
  {
    q: 'Travel kami berangkat rutin tiap bulan, bisa langganan?',
    a: 'Bisa. Kirim jadwal keberangkatan dan perkiraan jumlah jamaah per periode. Admin akan memeriksa stok serta waktu penyiapan setiap kali quotation dibuat.',
  },
]

export const Route = createFileRoute('/perlengkapan-haji-umroh')({
  loader: async () => {
    // Config failure falls back to null: the contact helpers carry hardcoded
    // defaults, and paid traffic must never land on a 500.
    const [config, testimonialsRaw, mukena, sarungLengan, mansetHaji, kaosKaki, handuk, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
      fetchCatalogProductCached('mukena', 'mukena'),
      fetchCatalogProductCached('sarung lengan haji umroh', 'sarung-lengan'),
      fetchCatalogProductCached('manset lengan cincin haji', 'manset-haji'),
      fetchCatalogProductCached('kaos kaki solat muslimah', 'kaos-kaki'),
      fetchCatalogProductCached('handuk mandi katun', 'handuk'),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
      products: { mukena, sarungLengan, mansetHaji, kaosKaki, handuk },
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Grosir Perlengkapan Jamaah Umroh & Haji')
    const description =
      'Supplier perlengkapan jamaah umroh dan haji tangan pertama untuk travel: kaos kaki, sarung lengan, manset, handuk. Harga grosir, bisa PO dan invoice resmi.'

    return {
      // Indexable: targets travel agencies searching for jamaah-kit suppliers
      // and doubles as a paid-ads destination.
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/perlengkapan-haji-umroh') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/perlengkapan-haji-umroh') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: TravelLandingPage,
})

// A real storefront product resolved in the loader, so each showcase card
// shows an actual photo and links to its product page.
type CatalogProduct = { name: string; slug: string; image: string | null }

// dropifyFetch deliberately never caches /storefront/products, but these
// showcase lookups don't need per-request freshness. Cache them at the route
// level so ad clicks don't pay four API round-trips of TTFB.
const CATALOG_PRODUCT_TTL_MS = 60 * 60 * 1000
const CATALOG_PRODUCT_MISS_TTL_MS = 5 * 60 * 1000
const catalogProductCache = new Map<string, { expiresAt: number; product: CatalogProduct | null }>()

async function fetchCatalogProductCached(search: string, cacheKey: string): Promise<CatalogProduct | null> {
  const cached = catalogProductCache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) return cached.product
  let product: CatalogProduct | null = null
  try {
    const response = await listStorefrontProducts({ search, per_page: 3 })
    const first = (response.items ?? [])[0]
    if (first) {
      const url = getProductPrimaryImageUrl(first)
      product = {
        name: first.name,
        slug: first.slug || first.id,
        image: url ? toExternalImage(url, PRODUCT_IMAGE_TRANSFORM) : null,
      }
    }
  } catch {
    product = null
  }
  const ttlMs = product ? CATALOG_PRODUCT_TTL_MS : CATALOG_PRODUCT_MISS_TTL_MS
  catalogProductCache.set(cacheKey, { expiresAt: Date.now() + ttlMs, product })
  return product
}

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation per komposisi paket',
    desc: 'Quotation merinci produk, jumlah, harga, dan ongkir agar biaya per jamaah dapat dihitung.',
  },
  {
    icon: faBoxesStacked,
    title: 'Beberapa item dalam satu quotation',
    desc: 'Kaos kaki, sarung lengan, manset, dan handuk dapat disusun dalam satu daftar kebutuhan.',
  },
  {
    icon: faCalendarCheck,
    title: 'Jadwal diperiksa sebelum pemesanan',
    desc: 'Kirim target penerimaan dan jadwal keberangkatan. Admin akan memeriksa stok, waktu penyiapan, dan estimasi perjalanan.',
  },
  {
    icon: faFileInvoice,
    title: 'PO dan invoice',
    desc: 'Sampaikan kebutuhan Purchase Order dan invoice untuk administrasi keuangan travel Anda.',
  },
]

const FIT_ITEMS = [
  'Travel umroh atau haji yang menyediakan paket perlengkapan untuk jamaahnya tiap keberangkatan.',
  'Tim pengadaan yang ingin mencantumkan beberapa jenis perlengkapan dalam satu quotation.',
  'Travel dengan jadwal keberangkatan rutin yang perlu memeriksa ketersediaan produk untuk setiap periode.',
  'Penyedia paket manasik atau oleh-oleh haji yang membeli dalam jumlah grosir.',
  'Toko perlengkapan haji dan umroh yang membeli barang untuk dijual kembali.',
  'Travel baru yang sedang menentukan isi paket, jumlah, dan target penerimaan.',
]

const OBJECTIONS = [
  {
    worry: 'Bagaimana memastikan jadwalnya cukup?',
    answer: 'Kirim tanggal keberangkatan dan target penerimaan. Admin akan memeriksa stok, waktu penyiapan, serta estimasi perjalanan sebelum memberi batas waktu pemesanan.',
  },
  {
    worry: 'Apakah isi paket dapat disesuaikan?',
    answer: 'Kirim daftar produk dan jumlah per paket. Admin akan memeriksa MOQ tiap kategori dan menghitung biaya per jamaah dalam quotation.',
  },
  {
    worry: 'Apakah pemesanan dapat memakai PO dan invoice?',
    answer: 'Bisa. Sampaikan kebutuhan Purchase Order dan invoice saat meminta quotation agar admin dapat menyiapkan dokumen untuk administrasi travel Anda.',
  },
  {
    worry: 'Data apa yang tersedia untuk perbandingan pemasok?',
    answer: 'Quotation memuat produk, jumlah, harga, dan ongkir. Tim Anda dapat membandingkan rincian tersebut dengan penawaran pemasok lain.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PRODUCT_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PRODUCT_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function TravelLandingPage() {
  const { config, testimonialImages, products, newsPosts } = Route.useLoaderData()

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, travel kami butuh perlengkapan jamaah umroh/haji. Jumlah jamaah sekitar ___ orang. Boleh minta penawaran harga per paketnya?'
  const softMessage = 'Halo Dropify, saya dari travel umroh/haji dan sedang mencari pemasok perlengkapan jamaah. Mohon informasi produk, MOQ, dan cara meminta quotation.'
  const ctaMessage = 'Halo Dropify, saya mau minta penawaran resmi perlengkapan jamaah untuk travel kami. Bisa kirim rincian item, harga, dan ongkirnya?'

  // Showcase cards from real storefront products; a card only renders when its
  // product resolves, so the section never shows placeholder imagery.
  const showcaseItems = [
    products.mukena && {
      product: products.mukena,
      title: 'Mukena',
      desc: 'Mukena untuk perlengkapan ibadah jamaah wanita. Periksa bahan, ukuran, potongan, dan detail hiasan per model.',
      tag: 'Paket jamaah wanita',
    },
    products.kaosKaki && {
      product: products.kaosKaki,
      title: 'Kaos Kaki Solat Muslimah',
      desc: 'Kaos kaki salat untuk jamaah wanita. Periksa bahan, panjang, ukuran, dan permukaan telapak pada model yang dipilih.',
      tag: 'Kaos kaki salat',
    },
    products.sarungLengan && {
      product: products.sarungLengan,
      title: 'Sarung Lengan Haji & Umroh',
      desc: 'Sarung lengan panjang untuk menjaga aurat jamaah wanita saat ihram dan ibadah harian di tanah suci.',
      tag: 'Penutup lengan',
    },
    products.mansetHaji && {
      product: products.mansetHaji,
      title: 'Manset Lengan Cincin Haji',
      desc: 'Manset lengan dengan cincin. Periksa bahan, ukuran, dan kekencangan karetnya pada sample sebelum menyusun paket jamaah.',
      tag: 'Model cincin',
    },
    products.handuk && {
      product: products.handuk,
      title: 'Handuk Katun',
      desc: 'Handuk katun untuk perlengkapan harian jamaah. Periksa ukuran, gramasi, warna, dan isi kemasan sebelum memilih model.',
      tag: 'Pelengkap paket',
    },
  ].filter(Boolean) as Array<{ product: CatalogProduct; title: string; desc: string; tag: string }>

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Pengadaan perlengkapan jamaah untuk travel. Quotation disusun dari isi paket dan jumlah jamaah.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta penawaran"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      {/* Hero — hook + per-jamaah kit card */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              <FontAwesomeIcon icon={faKaaba} className="h-3 w-3" />
              Untuk travel umroh & haji
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Susun perlengkapan jamaah <Mark>dalam satu quotation.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Dropify menyuplai kaos kaki salat, sarung lengan, manset, dan handuk untuk kebutuhan travel. Sebutkan jumlah jamaah, komposisi paket, kota tujuan, serta target penerimaan agar admin dapat menyusun quotation.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta penawaran per paket"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href={waLink(softMessage)}
                {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: PRODUCT_NAME })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Tanyakan produk dan MOQ →
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Sample dapat dipesan dengan harga satuan dan ongkir sebelum pemesanan rombongan.</p>
          </div>

          {/* Hero visual: example per-jamaah kit */}
          <div className="relative">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 shadow-2xl">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-300">Contoh komposisi paket per jamaah</p>
              <ul className="mt-4 space-y-3">
                {[
                  'Mukena untuk jamaah wanita',
                  'Kaos kaki salat muslimah',
                  'Sarung lengan haji & umroh panjang',
                  'Manset lengan atau kaos tangan',
                  'Handuk katun untuk bekal harian',
                  'Head buff / penutup kepala',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 rounded-xl bg-white/5 px-4 py-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                      <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5 text-white" />
                    </span>
                    <p className="text-base leading-relaxed text-slate-200">{item}</p>
                  </li>
                ))}
              </ul>
              <p className="mt-5 border-t border-white/10 pt-4 text-sm text-slate-400">
                Kirim komposisi paket keberangkatan yang dibutuhkan agar admin dapat memeriksa setiap item.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AdNewsProofSection posts={newsPosts} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Who it's for */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kebutuhan travel yang kami layani</h2>
            <p className="mt-2 text-base text-slate-500">Halaman ini membantu travel dan tim pengadaan menyusun perlengkapan jamaah dalam jumlah besar.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Informasi pengadaan yang tersedia untuk travel</h2>
            <p className="mt-2 text-base text-slate-500">Quotation, pemeriksaan jadwal, sample, dan dokumen administrasi dibahas sebelum pemesanan.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 bg-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Cara memesan untuk travel Anda</h2>
            <p className="mt-2 text-base text-slate-500">Proses dimulai dari rincian kebutuhan dan berakhir saat barang diserahkan kepada ekspedisi.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: '1', title: 'Kirim kebutuhan', desc: 'Sebutkan jumlah jamaah, komposisi paket, dan target penerimaan.' },
              { n: '2', title: 'Periksa quotation', desc: 'Admin merinci produk, jumlah, harga, dan ongkir dalam quotation.' },
              { n: '3', title: 'Periksa sample', desc: 'Pesan sample jika tim perlu memeriksa bahan, ukuran, atau jahitan.' },
              { n: '4', title: 'Konfirmasi dan pengiriman', desc: 'Setelah pesanan dikonfirmasi, admin menyiapkan barang dan memberikan resi pengiriman.' },
            ].map((step) => (
              <div key={step.n} className="rounded-2xl border border-slate-100 bg-white p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-sm font-black text-slate-900">{step.n}</span>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product showcase from real storefront products */}
      {showcaseItems.length > 0 && (
        <section className="py-14 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="mb-8 max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Item perlengkapan jamaah yang kami suplai</h2>
              <p className="mt-2 text-base text-slate-500">Bandingkan fungsi, bahan, ukuran, dan MOQ setiap produk sebelum menyusun paket jamaah.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {showcaseItems.map((item) => (
                <div key={item.title} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg sm:flex-row">
                  {item.product.image && (
                    <div className="relative overflow-hidden bg-white sm:w-44 sm:flex-shrink-0">
                      <img src={item.product.image} alt={item.title} width="400" height="400" loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" />
                      <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">{item.tag}</span>
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                    <p className="mt-1.5 flex-1 text-base leading-relaxed text-slate-500">{item.desc}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                      <WhatsAppButton
                        message={`Halo Dropify, travel kami membutuhkan ${item.title} untuk perlengkapan jamaah. Mohon periksa MOQ, stok, dan harga untuk kebutuhan ___ paket ke ___.`}
                        label="Periksa stok dan harga"
                        itemName={item.title}
                        className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                      />
                      <a href={`/produk/${item.product.slug}`} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
                        Lihat detail produk →
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { icon: faBoxesStacked, text: 'Stok dan waktu penyiapan diperiksa sesuai jumlah jamaah' },
                { icon: faCalendarCheck, text: 'Jadwal diperiksa dari target penerimaan dan keberangkatan' },
                { icon: faTruckFast, text: 'Pengiriman antarkota disertai nomor resi' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <FontAwesomeIcon icon={b.icon} className="h-4 w-4" />
                  </span>
                  <p className="text-sm font-medium leading-snug text-slate-600">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      {/* Objection handling */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Hal yang perlu dikonfirmasi sebelum PO</h2>
            <p className="mt-2 text-base text-slate-500">Periksa MOQ, sample, komposisi paket, dokumen, dan jadwal pengiriman.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Kirim rancangan paket dan jadwal travel kepada admin sebelum menerbitkan PO.</p>
            <WhatsAppButton
              message={softMessage}
              label="Tanyakan kepada admin"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* SEO prose */}
      <section className="bg-white py-14 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900">Supplier perlengkapan jamaah umroh dan haji untuk travel</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              Dropify menyuplai <strong className="font-semibold text-slate-800">perlengkapan jamaah haji dan umroh</strong> dalam jumlah grosir, termasuk mukena, kaos kaki salat muslimah, sarung lengan, manset, kaos tangan, handuk, dan head buff. Kirim komposisi serta jumlah jamaah agar admin dapat merinci biaya per paket melalui quotation.
            </p>
            <p>
              Kirim jumlah jamaah, komposisi paket, kota tujuan, target penerimaan, dan tanggal keberangkatan. Admin akan menyiapkan quotation serta estimasi waktu penyiapan dan perjalanan. Untuk kebutuhan berkala, cantumkan perkiraan jumlah per periode agar ketersediaan setiap produk dapat diperiksa. Pemesanan dapat memakai PO dan invoice sesuai kebutuhan administrasi travel.
            </p>
            <p>
              Pengiriman dapat ditujukan ke kantor travel di berbagai kota di Indonesia. Pesan sample dengan harga satuan dan ongkir jika tim Anda perlu memeriksa bahan, ukuran, atau jahitan sebelum pemesanan rombongan.
            </p>
            <p>
              Halaman ini khusus untuk travel yang menyiapkan paket per jamaah. Jika Anda membeli untuk toko oleh-oleh haji, toko busana muslim, atau toko perlengkapan ibadah, buka halaman{' '}
              <a href="/grosir-perlengkapan-haji-umroh" className="font-semibold text-blue-600 hover:text-blue-700">
                grosir perlengkapan haji dan umroh
              </a>{' '}
              kami.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Minta quotation perlengkapan untuk jamaah</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Sebutkan jumlah jamaah, komposisi paket, kota tujuan, target penerimaan, dan tanggal keberangkatan. Admin akan menyusun quotation berdasarkan rincian tersebut.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation melalui WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Cantumkan kebutuhan PO atau invoice pada pesan pertama.
          </p>

          {/* Legitimacy: accepted payment methods */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/perlengkapan-haji-umroh" />

      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pengadaan travel melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PRODUCT_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-400">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-400" />
        </div>
      </footer>
    </div>
  )
}

function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan Dropify
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan yang telah kami tangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar perlengkapan jamaah</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'travel' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
